// @ts-nocheck
import { referenceMaps } from '../global/utils/backboneUtils';
import React, { useState, useEffect } from 'react';
import DxPopup from '../global/components/DxPopup';
import { Switch, Route, useRouteMatch, Redirect } from 'react-router-dom';
import dx from '../global/dx';
import RenderServerSidePage from './RenderServerSidePage';
import { Provider } from 'react-redux';
import store from '../global/store/createStore';
import ActiveHeader from '../global/components/ActiveHeader';
import LoadingIndicator from '../global/components/LoadingIndicator';
import { t } from '../global/locale';
import ErrorBoundary from '../global/ErrorBoundary';
// Ensure the UMD bundle is part of the legacy build graph for deterministic loading
// eslint-disable-next-line import/no-unresolved
import '../vendor/react19/react19-module.js';

const getDefaultPartnerRedirect = baseUrl => {
    if (dx.utils.hasRole(['administrator']) && dx.utils.getPartnerId() === 1) {
        return `${baseUrl}/dashboard`;
    } else if (dx.utils.hasModule('dashboard') && dx.utils.hasRole(['kulturhussjef']) && dx.utils.hasSSH()) {
        return `${baseUrl}/dashboard`;
    } else if (
        dx.utils.hasModule('culture') &&
        dx.utils.hasRole(['kulturhussjef', 'culture_full_read_only', 'culture_events_read_only'])
    ) {
        return `${baseUrl}/culture`;
    } else {
        return `${baseUrl}/shows`;
    }
};

// A failed dynamic import after a deploy usually means the hashed chunk was
// removed (the client is on a stale build, or this chunk is missing from the
// current build). A full reload fetches the fresh index.html + chunk map and
// recovers the stale-client case. Guard with sessionStorage, keyed PER MODULE,
// so we reload at most once per chunk and then surface the error to the
// ErrorBoundary (Sentry + a manual reload button) instead of looping. Per-module
// (rather than one global flag) matters when only a single chunk is missing from
// the current build: bouncing between a working module and the broken one must
// not re-arm the broken one's reload and cause reload-thrash. NB: requires nginx
// to return 404 (not index.html/200) for missing /static/ assets, otherwise
// import() never rejects (see sc-11245).
// (The ErrorBoundary's service-worker path may trigger one further reload when
// a new SW is waiting; that is gated on a real SW update, not a blind loop.)
const CHUNK_RELOAD_PREFIX = 'dxweb:chunkReloaded:';

// sessionStorage can throw in private mode / locked-down webviews; degrade to
// "reload disabled" rather than turning a good import into a failure.
const chunkReload = {
    seen: key => {
        try {
            return sessionStorage.getItem(CHUNK_RELOAD_PREFIX + key);
        } catch (e) {
            return null;
        }
    },
    // Returns true only if the guard persisted. If storage is blocked we must
    // NOT reload, because the guard wouldn't survive the reload and we'd loop.
    mark: key => {
        try {
            sessionStorage.setItem(CHUNK_RELOAD_PREFIX + key, '1');
            return true;
        } catch (e) {
            return false;
        }
    },
    clear: key => {
        try {
            sessionStorage.removeItem(CHUNK_RELOAD_PREFIX + key);
        } catch (e) {
            /* storage blocked */
        }
    },
};

const lazyWithReload = (importFn, key) =>
    React.lazy(() =>
        importFn()
            .then(mod => {
                chunkReload.clear(key);
                return mod;
            })
            .catch(err => {
                // Reload once to recover a stale build, but only if the guard
                // actually persisted; otherwise the reload would just loop.
                if (!chunkReload.seen(key) && chunkReload.mark(key)) {
                    window.location.reload();
                    return new Promise(() => {});
                }
                throw err;
            })
    );

const AsyncModuleLoading = (Component, title) => () => {
    return (
        <ErrorBoundary title={title}>
            <React.Suspense
                fallback={
                    <Provider store={store}>
                        <ActiveHeader title={title} />
                        <LoadingIndicator message={t('Laster inn modul')} />
                    </Provider>
                }
            >
                <Component />
            </React.Suspense>
        </ErrorBoundary>
    );
};

const AsyncCultureModule = AsyncModuleLoading(
    lazyWithReload(() => import('../modules/culture/main'), 'culture'),
    t('Arrangementer')
);
const AsyncShowsModule = AsyncModuleLoading(
    lazyWithReload(() => import('../modules/shows/main'), 'shows'),
    t('Arrangementer')
);
const AsyncOrdersModule = AsyncModuleLoading(
    lazyWithReload(() => import('../modules/orders/main'), 'orders'),
    t('Prosjekt')
);
const AsyncUserAdminModule = AsyncModuleLoading(
    lazyWithReload(() => import('../modules/useradmin/main'), 'useradmin'),
    t('Brukere')
);
const AsyncCampaignsModule = AsyncModuleLoading(
    lazyWithReload(() => import('../modules/campaigns/main'), 'campaigns'),
    t('Markedsføring')
);
const AsyncProductsModule = AsyncModuleLoading(
    lazyWithReload(() => import('../modules/products/main'), 'products'),
    t('Produkter')
);
const AsyncFilmSchedulerModule = AsyncModuleLoading(
    lazyWithReload(() => import('../modules/filmscheduler/main'), 'filmscheduler'),
    t('Filmprogrammering')
);
const AsyncRentersModule = AsyncModuleLoading(
    lazyWithReload(() => import('../modules/renters/main'), 'renters'),
    t('Leietakere')
);
const AsyncMediaBaseModule = AsyncModuleLoading(
    lazyWithReload(() => import('../modules/mediabase/main'), 'mediabase'),
    t('Mediabase')
);
const AsyncPartnerAdminModule = AsyncModuleLoading(
    lazyWithReload(() => import('../modules/partneradmin/main'), 'partneradmin'),
    t('Partnere')
);
const AsyncReportsModule = AsyncModuleLoading(
    lazyWithReload(() => import('../modules/reports/main'), 'reports'),
    t('Rapporter')
);
const AsyncPartnerProfileModule = AsyncModuleLoading(
    lazyWithReload(() => import('../modules/partnerprofile/main'), 'partnerprofile'),
    t('Innstillinger')
);
const AsyncReact19Module = AsyncModuleLoading(
    lazyWithReload(() => import('../modules/react19-module/main'), 'react19-module'),
    'React 19 Demo'
);
const AsyncReportsAdminModule = AsyncModuleLoading(
    lazyWithReload(() => import('../modules/reportsadmin/main'), 'reportsadmin'),
    t('Rapportadmin')
);

const RenderModule = () => {
    const [prevModule, setPrevModule] = useState(null);
    const { path, url } = useRouteMatch();
    const matchModule = useRouteMatch({
        path: `${path}/:module`,
        strict: false,
        sensitive: false,
    });
    useEffect(() => {
        if (matchModule && matchModule.params && matchModule.params.module !== prevModule) {
            referenceMaps.clearAllRefMaps();
            setPrevModule(matchModule.params.module);
        }
    });
    let partnerId = dx.utils.getPartnerId();
    return (
        <Switch>
            <Route path={`${path}/default`}>
                <Redirect to={getDefaultPartnerRedirect(url)} />
            </Route>
            <Route path={`${path}/dashboard`}>
                <RenderServerSidePage
                    url={`/api/v1/partners/${partnerId}/dashboardhtml`}
                    title="Dashboard"
                    sidebarBody={true}
                />
            </Route>
            <Route path={`${path}/dashboard-legacy`}>
                <RenderServerSidePage
                    url={`/api/v1/partners/${partnerId}/dashboardhtml-legacy`}
                    title="Dashboard, gammelt"
                />
            </Route>
            <Route path={`${path}/culture`}>
                <AsyncCultureModule />
            </Route>
            <Route path={`${path}/crm`}>
                <RenderServerSidePage
                    url={`/api/v1/partners/${partnerId}/crmhtml`}
                    title={t('Kunder')}
                    sidebarBody={true}
                />
            </Route>
            <Route path={`${path}/renters/:renterId/dashboard`}>
                <RenderServerSidePage
                    url={`/api/v1/partners/${partnerId}/renter-dashboardhtml`}
                    title={t('Dashbord')}
                    sidebarBody={true}
                />
            </Route>
            <Route path={`${path}/renters/:renterId/shows`}>
                <AsyncShowsModule />;
            </Route>
            <Route path={`${path}/shows`}>
                <AsyncShowsModule />
            </Route>
            <Route path={`${path}/orders`}>
                <AsyncOrdersModule />
            </Route>
            <Route path={`${path}/useradmin`}>
                <AsyncUserAdminModule />
            </Route>
            <Route path={`${path}/campaigns`}>
                <AsyncCampaignsModule />
            </Route>
            <Route path={`${path}/products`}>
                <AsyncProductsModule />
            </Route>
            <Route path={`${path}/filmscheduler`}>
                <AsyncFilmSchedulerModule />
            </Route>
            <Route path={`${path}/renteradmin`}>
                <AsyncRentersModule />
            </Route>
            <Route path={`${path}/mediabase`}>
                <AsyncMediaBaseModule />
            </Route>
            <Route path={`${path}/partneradmin`}>
                <AsyncPartnerAdminModule />
            </Route>
            <Route path={`${path}/report`}>
                <AsyncReportsModule />
            </Route>
            <Route path={`${path}/partnerprofile`}>
                <AsyncPartnerProfileModule />
            </Route>
            <Route path={`${path}/react19-module`}>
                <AsyncReact19Module />
            </Route>
            <Route path={`${path}/booking-calendar`}>
                <Provider store={store}>
                    <ActiveHeader title={t('Bookingkalender')} />
                    <div style={{ height: 'calc(100vh - 80px)', overflow: 'hidden', position: 'relative' }}>
                        <react19-module initial-route="/booking-calendar" />
                    </div>
                </Provider>
            </Route>
            <Route
                path={`${path}/globalRenterIntegrations/:globalRenterId`}
                render={({ match, history }) => (
                    <Provider store={store}>
                        <ActiveHeader
                            title={t('Utvidelser')}
                            onBackButtonClick={() => history.push(`${url}/renteradmin`)}
                        />
                        <div style={{ height: 'calc(100vh - 80px)', overflow: 'hidden', position: 'relative' }}>
                            <react19-module
                                initial-route={`/global-renter-integrations/${match.params.globalRenterId}`}
                            />
                        </div>
                    </Provider>
                )}
            />
            <Route path={`${path}/reportsadmin`}>
                <AsyncReportsAdminModule />
            </Route>
            <Route>
                <h1>{t('Ukjent adresse (RenderModule)')}</h1>
            </Route>
        </Switch>
    );
};
export default RenderModule;
